import React, { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownSelect,
  IconButton,
  LANGUAGE_ENGLISH_NAMES,
  makeStyles,
  TranslationStatusChip
} from '@paintscout/ui'
import type { DialogProps, DropdownSelectOption, SupportedLang } from '@paintscout/ui'
import type { TranslationCompletionStatus } from '@paintscout/util/builder/quote/util/translation-status'
import type { QuoteDocument } from 'paintscout'
import CloseIcon from '@material-ui/icons/Close'
import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import type { ApolloError } from 'apollo-client'
import { useSnackbar } from 'notistack'
import * as Sentry from '@sentry/react'
import { track } from 'shared/util/analytics'

// Pre-bakes quote.translations[lang] server-side and returns the per-language completion status
// plus the already-persisted quote, so the freshly pre-baked content can be downloaded straight
// from the mutation response — no separate quote(id) round trip for the same document.
const REFRESH_QUOTE_TRANSLATIONS = gql`
  mutation RefreshQuoteTranslations($quoteId: String!, $companyId: String!) {
    refreshQuoteTranslations(quoteId: $quoteId, companyId: $companyId) {
      success
      translationStatus {
        lang
        status
      }
      quote
    }
  }
`

const useStyles = makeStyles((theme) => ({
  overflowVisible: {
    overflow: 'visible'
  },
  alert: { marginTop: theme.spacing() },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing()
  }
}))

export interface DownloadWorkOrderDialogProps extends Omit<DialogProps, 'onConfirm'> {
  availableLangs: string[]
  /** The quote being downloaded; used to generate translations and as the base for the PDF. */
  quote: QuoteDocument
  companyId: string
  /**
   * Per-language pre-bake status. If the selected language is not `complete`, generation is
   * offered inline (you'd only ever generate before downloading) before the download is allowed.
   */
  translationStatus?: Record<string, TranslationCompletionStatus>
  /**
   * Called with the language code and the freshest quote document. When a translation is
   * generated in-dialog the mutation response already carries the persisted quote, so the
   * download reflects it without a page refresh or a separate re-fetch.
   */
  onConfirm: (lang: string | undefined, quote: QuoteDocument) => void
  onCancel: () => void
}

const ENGLISH_OPTION: DropdownSelectOption = {
  label: LANGUAGE_ENGLISH_NAMES['en'],
  value: 'en'
}

type LangStatusEntry = { lang: string; status: TranslationCompletionStatus }

// Merge the per-language statuses a refresh just returned into the existing status map, keyed
// by lang. Entries can be null over the wire (nullable list item), hence the type guard rather
// than `.filter(Boolean)` (which doesn't narrow past `| null` on its own).
function mergeLangStatus(
  current: Record<string, TranslationCompletionStatus>,
  entries: Array<LangStatusEntry | null | undefined>
): Record<string, TranslationCompletionStatus> {
  const merged = { ...current }
  for (const entry of entries) {
    if (entry) merged[entry.lang] = entry.status
  }
  return merged
}

function DownloadWorkOrderDialog({
  availableLangs,
  quote,
  companyId,
  translationStatus = {},
  onConfirm,
  onCancel,
  ...dialogProps
}: DownloadWorkOrderDialogProps) {
  const classes = useStyles()
  const apolloClient = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()

  const langOptions: DropdownSelectOption[] = [
    ENGLISH_OPTION,
    ...availableLangs.map((lang) => ({
      label: LANGUAGE_ENGLISH_NAMES[lang as SupportedLang],
      value: lang
    }))
  ]

  const [selectedLang, setSelectedLang] = useState<DropdownSelectOption>(ENGLISH_OPTION)
  // Status starts from the prop, but is updated in place after an in-dialog generation so the
  // estimator can download right away rather than reopening or refreshing.
  const [status, setStatus] = useState<Record<string, TranslationCompletionStatus>>(translationStatus)
  // The quote handed to onConfirm — swapped for a freshly re-fetched copy once a translation is
  // generated so the downloaded PDF contains the new content.
  const [currentQuote, setCurrentQuote] = useState<QuoteDocument>(quote)
  const [generating, setGenerating] = useState(false)
  // Set once an in-dialog generation attempt for the selected language has failed, so we can
  // point the estimator to support instead of leaving them to guess why Download isn't working.
  // Cleared on language change since a fresh language hasn't failed yet.
  const [attemptFailed, setAttemptFailed] = useState(false)

  const lang = selectedLang.value
  const langLabel = LANGUAGE_ENGLISH_NAMES[lang as SupportedLang] ?? lang

  // English never needs generation; a non-English language must be pre-baked ('complete')
  // before its PDF is downloaded, otherwise the estimator could get untranslated content (or,
  // if generated live inside the ~15s render window, a silent English fallback — #20847).
  const needsGeneration = lang !== 'en' && status[lang] !== 'complete'

  // Title pill reflecting the selected language's state: "Generating…" while producing,
  // "<Lang> Generated" once ready, and "<Lang> Not Generated" (warning) when it still needs
  // generating. English never shows a chip.
  const chipStatus = generating
    ? 'generating'
    : lang === 'en'
      ? null
      : status[lang] === 'complete'
        ? 'translated'
        : status[lang] === 'partial'
          ? 'partial'
          : 'untranslated'

  // Pre-bake the selected language and return the freshest quote document. Resolves to the
  // persisted quote on success, or null if generation didn't complete (already surfaced to the
  // user) so the caller knows not to proceed with the download.
  async function generateTranslation(): Promise<QuoteDocument | null> {
    // Fail fast without a network call (and without incurring a wasted Lambda invocation) if the
    // caller was ever mounted without a companyId — the resolver would reject it anyway, but
    // there's no reason to pay for the round trip to find that out.
    if (!companyId) {
      setAttemptFailed(true)
      Sentry.captureMessage('DownloadWorkOrderDialog rendered without a companyId', {
        tags: { source: 'DownloadWorkOrderDialog.generateTranslation', lang, quoteId: quote._id }
      })
      enqueueSnackbar(`Unable to generate ${langLabel} translation. Please try again.`, { variant: 'error' })
      return null
    }

    const { data } = await apolloClient.mutate<{
      refreshQuoteTranslations?: {
        success: boolean
        translationStatus?: Array<LangStatusEntry | null> | null
        quote?: QuoteDocument | null
      } | null
    }>({
      mutation: REFRESH_QUOTE_TRANSLATIONS,
      variables: { quoteId: quote._id, companyId }
    })

    const result = data?.refreshQuoteTranslations
    const nextStatus = result?.translationStatus ?? []
    setStatus((current) => mergeLangStatus(current, nextStatus))

    // The mutation can resolve without throwing yet still not have produced the translation
    // (AI call failed/timed out and was swallowed server-side). Trust the returned status.
    // Note: `result.success` means *every* enabled language reached "complete" (it also
    // retries languages other than the one selected here) — that's the right semantic for
    // the admin bulk-retry action, but checking it here would block (or, worse, silently skip
    // re-generation on a later click — see below) this specific language's download just
    // because some other, unrelated language failed. Only this language's own status matters.
    const langStatus = nextStatus.find((entry) => entry?.lang === lang)?.status
    if (langStatus !== 'complete' || !result?.quote) {
      setAttemptFailed(true)
      // No exception was thrown here — the mutation itself resolved fine, the AI generation just
      // didn't complete (or the persisted quote wasn't returned) server-side — so capture a
      // breadcrumb explicitly rather than relying on the catch block in handleDownload, which
      // never sees this case (#21747).
      Sentry.captureMessage('refreshQuoteTranslations resolved without completing the translation', {
        tags: { source: 'DownloadWorkOrderDialog.generateTranslation', lang, quoteId: quote._id, companyId }
      })
      enqueueSnackbar(`Unable to generate ${langLabel} translation. Please try again.`, { variant: 'error' })
      return null
    }

    // The mutation already returns the persisted quote with translations applied, so there's no
    // need for a second quote(id) fetch just to see the same document.
    setCurrentQuote(result.quote)
    track('work_order_translation_generated', { quoteId: quote._id, lang })
    return result.quote
  }

  // Single primary action: generation is tied into the download — if the selected language
  // isn't ready we generate it first, then download the freshly translated quote in the same
  // click. No separate "Generate" step or permanent block on retrying.
  async function handleDownload() {
    if (!needsGeneration) {
      onConfirm(lang === 'en' ? undefined : lang, currentQuote)
      return
    }

    setGenerating(true)
    try {
      const fresh = await generateTranslation()
      if (fresh) {
        onConfirm(lang, fresh)
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { source: 'DownloadWorkOrderDialog.handleDownload', lang } })
      setAttemptFailed(true)
      const code = (err as ApolloError)?.graphQLErrors?.[0]?.extensions?.code
      enqueueSnackbar(
        code === 'TRANSLATION_UNAVAILABLE'
          ? 'Translation is not available right now. Please contact support.'
          : code === 'RATE_LIMITED'
            ? 'Translation is already being generated for this quote. Please wait a moment and try again.'
            : 'Unable to generate translation. Please try again.',
        { variant: 'error' }
      )
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Dialog {...dialogProps} classes={{ paper: classes.overflowVisible }}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={onCancel} disabled={generating}>
            <CloseIcon />
          </IconButton>
        }
      >
        <span className={classes.titleRow}>
          Download Work Order
          {chipStatus && <TranslationStatusChip status={chipStatus} language={langLabel} />}
        </span>
      </DialogTitle>
      <DialogContent className={classes.overflowVisible}>
        <DropdownSelect
          variant="single"
          label="Language"
          options={langOptions}
          value={selectedLang}
          disabled={generating}
          onChange={(option) => {
            setAttemptFailed(false)
            setSelectedLang(option as DropdownSelectOption)
          }}
        />
        {generating && (
          <Alert severity="info" className={classes.alert}>
            Generating {langLabel} translation — this will download automatically once it&apos;s ready.
          </Alert>
        )}
        {!generating && needsGeneration && attemptFailed && (
          <Alert severity="warning" className={classes.alert}>
            {langLabel} translation is incomplete. Please{' '}
            <a href="mailto:support@paintscout.com">contact PaintScout Support</a> if retrying doesn&apos;t work.
          </Alert>
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          <Button variant="text" onClick={onCancel} disabled={generating}>
            Cancel
          </Button>
        }
      >
        <Button variant="contained" color="primary" loading={generating} disabled={generating} onClick={handleDownload}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DownloadWorkOrderDialog
